import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { DeleteCompanyMode } from '@ip/shared-types';
import { t } from '@/i18n';
import { useCompaniesApi } from '@/services/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface DeleteCompanyDialogProps {
  open: boolean;
  nip: string;
  companyName: string;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteCompanyDialog({
  open,
  nip,
  companyName,
  onClose,
  onDeleted,
}: DeleteCompanyDialogProps) {
  const [mode, setMode] = useState<DeleteCompanyMode>('unassign');
  const [confirmNip, setConfirmNip] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { deleteCompany } = useCompaniesApi();

  const resetAndClose = () => {
    setMode('unassign');
    setConfirmNip('');
    onClose();
  };

  const handleConfirm = async () => {
    try {
      setDeleting(true);
      const result = await deleteCompany(nip, mode);
      toast.success(
        t('companies.deleted', {
          nip,
          invoices: result.invoices,
          contractors: result.contractors,
          tags: result.tags,
          employees: result.employees,
          vehicles: result.vehicles,
          crmAccounts: result.crmAccounts,
          crmLeads: result.crmLeads,
        }),
      );
      if (result.s3Skipped && result.s3Skipped.length > 0) {
        toast.warning(t('companies.deleteS3Warning'));
      }
      resetAndClose();
      onDeleted();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('errors.deleteFailed'));
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('companies.deleteWarningTitle', { nip: companyName ? `${companyName} (${nip})` : nip })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('companies.deleteWarningBody')}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as DeleteCompanyMode)}>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="unassign" id="delete-mode-unassign" className="mt-1" />
              <Label htmlFor="delete-mode-unassign" className="font-normal">
                {t('companies.deleteModeUnassign')}
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="purge" id="delete-mode-purge" className="mt-1" />
              <Label htmlFor="delete-mode-purge" className="font-normal">
                {t('companies.deleteModePurge')}
              </Label>
            </div>
          </RadioGroup>

          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('companies.confirmNipLabel')}
            </Label>
            <Input
              value={confirmNip}
              onChange={(e) => setConfirmNip(e.target.value)}
              placeholder={t('companies.confirmNipPlaceholder')}
              autoComplete="off"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={confirmNip !== nip || deleting}
            onClick={handleConfirm}
          >
            {deleting && <Loader2 className="size-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
